import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Ticket, Mail, ShieldCheck, BarChart3, Check } from "lucide-react";

const FEATURES = [
  { i: ShieldCheck, a: "Built for", b: "Scare events" },
  { i: Ticket, a: "Easy ticketing", b: "For your visitors" },
  { i: BarChart3, a: "Powerful tools", b: "For attractions" },
];

export default function MarketingHome() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) { setStatus("error"); return; }
    setStatus("loading");
    try {
      await api.post("/public/register-interest", { email: email.trim() });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <div className="flex-1 w-full max-w-3xl mx-auto px-6 py-16 md:py-20">
        {/* Logo + tagline */}
        <div className="text-center st-fade-up" data-testid="marketing-logo">
          <div className="st-brand leading-none inline-flex items-start justify-center" style={{ fontSize: "clamp(44px,10vw,88px)" }}>
            SCARE<span className="text-[#dc2626]">TICKETS</span>
            <span className="st-mono text-[#dc2626] self-end mb-[0.74em] ml-1.5" style={{ fontSize: "0.2em", letterSpacing: 0 }}>.CO.UK</span>
          </div>
          <div className="flex items-center justify-center gap-4 mt-3">
            <span className="h-px w-10 md:w-14 bg-gradient-to-r from-transparent to-[#dc2626]" />
            <span className="text-[#9a9a9a] uppercase tracking-[0.32em] text-[11px] md:text-sm">Powering Scare Attractions</span>
            <span className="h-px w-10 md:w-14 bg-gradient-to-l from-transparent to-[#dc2626]" />
          </div>
        </div>

        {/* Ticket icon */}
        <div className="flex justify-center mt-12 md:mt-16">
          <Ticket size={52} strokeWidth={1.4} className="text-[#dc2626] -rotate-12" />
        </div>

        {/* Headline */}
        <div className="mt-10 md:mt-14 text-center">
          <h1 className="st-brand leading-[0.95] text-5xl md:text-7xl" data-testid="marketing-headline">
            <span className="text-white">New Attractions</span><br />
            <span className="text-[#dc2626]">Being Onboarded Soon.</span>
          </h1>
          <p className="text-[#9a9a9a] text-lg md:text-xl mt-6 max-w-xl mx-auto leading-relaxed">
            ScareTickets.co.uk is currently in limited launch mode and not accepting new attractions at this time.
          </p>
        </div>

        <hr className="border-[#262626] my-9 md:my-11" />

        {/* Register interest */}
        <div className="grid md:grid-cols-2 gap-7 md:gap-10 items-start">
          <div className="flex gap-4">
            <Mail className="text-[#dc2626] shrink-0 mt-1" size={28} strokeWidth={1.6} />
            <div>
              <div className="font-bold uppercase tracking-wide text-base md:text-lg">Register Your Interest</div>
              <p className="text-[#8a8a8a] text-sm mt-1.5 leading-relaxed">Be the first to know when we start onboarding new attractions.</p>
            </div>
          </div>

          {status === "done" ? (
            <div className="flex items-center gap-3 bg-[#0d1f12] border border-[#1f4429] rounded-lg px-4 py-3.5" data-testid="interest-success">
              <Check className="text-[#22c55e] shrink-0" size={18} />
              <span className="text-sm text-[#bfe9cc]">You&apos;re on the list — we&apos;ll be in touch.</span>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col" data-testid="interest-form">
              <div className="flex">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                  placeholder="Enter your email address"
                  data-testid="interest-email-input"
                  className="flex-1 min-w-0 bg-[#161616] border border-[#2b2b2b] border-r-0 rounded-l-lg px-4 py-3 text-sm text-white placeholder:text-[#6a6a6a] focus:outline-none focus:border-[#dc2626] transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="interest-submit"
                  className="bg-[#dc2626] hover:bg-[#b91c1c] disabled:opacity-60 text-white font-semibold uppercase tracking-wide text-sm px-6 rounded-r-lg transition-colors"
                >
                  {status === "loading" ? "…" : "Register"}
                </button>
              </div>
              {status === "error" && <span className="text-xs text-[#fca5a5] mt-2" data-testid="interest-error">Please enter a valid email address.</span>}
            </form>
          )}
        </div>

        {/* Feature strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 mt-14 md:mt-16">
          {FEATURES.map(({ i: Icon, a, b }, idx) => (
            <div key={a} className={`flex items-center gap-3.5 py-3 sm:py-0 ${idx > 0 ? "sm:border-l sm:border-[#262626] sm:pl-6" : ""} ${idx < FEATURES.length - 1 ? "sm:pr-6" : ""}`}>
              <Icon className="text-[#dc2626] shrink-0" size={30} strokeWidth={1.5} />
              <div className="uppercase leading-tight">
                <div className="font-bold text-sm">{a}</div>
                <div className="font-bold text-sm text-[#cfcfcf]">{b}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 py-7 text-center">
        <p className="text-xs text-[#6a6a6a]">© ScareTickets.co.uk {new Date().getFullYear()}. All rights reserved.</p>
        <Link to="/login" className="text-[11px] text-[#4a4a4a] hover:text-[#9a9a9a] transition-colors mt-2 inline-block" data-testid="attraction-login-link">
          Attraction owner login
        </Link>
      </footer>
    </div>
  );
}
